import {Theme} from '@mui/material'
import {createStyles, makeStyles} from '@mui/styles'

const navbarOpenWidth = 260
const navbarCloseWidth = 64

export const useAlertStyles = makeStyles({
  root: {boxShadow: 'none'},
  standardError: {
    background: '#FCD0CF',
  },
  standardSuccess: {
    backgroundColor: '#C5F2C7',
  },
})

export const useAvatarStyles = makeStyles(() => {
  const uploadDefault = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 70,
    height: 70,
    borderColor: '#D2D6EB',
    borderRadius: 0,
  }
  const uploadPseudoDefault = {
    position: 'absolute',
    content: `"+"`,
    right: 13,
    bottom: 13,
    width: 20,
    height: 20,
    lineHeight: '20px',
    color: '#EFF0F6',
    background: '#B9B9B9',
    borderRadius: '50%',
  }

  return createStyles({
    uploadBtn: {
      ...uploadDefault,
      background: '#EFF0F6',
      borderRadius: '50%',
      '&:hover': {
        '&::after': {
          ...uploadPseudoDefault,
          right: 13,
          bottom: 13,
        },
      },
    },
    uploadAvatar: {
      ...uploadDefault,
      '& img': {
        borderRadius: '50%',
      },
      '&:hover': {
        '&::after': {
          ...uploadPseudoDefault,
          right: 0,
          bottom: 0,
        },
      },
    },
  })
})

/*
  Side menu in the user dashboard's style (espy-dashboard style/common.ts):
  dark — the navy panel, items in a soft blue gradient with an inset accent
  bar when selected; light — a white panel, the selected item on the light
  blue tint. Menu.tsx puts `light` or `dark` on the Drawer, following the
  admin theme switch (hook/useAdminTheme).
*/
const menuDark = {
  paper: '#0B081D',
  divider: 'rgba(255,255,255,.08)',
  text: '#A9B2CC',
  textActive: '#FFFFFF',
  hoverBg: 'linear-gradient(90deg, rgba(45,137,244,.18), rgba(45,137,244,.03))',
  selectedBg: 'linear-gradient(90deg, rgba(45,137,244,.25), rgba(45,137,244,.05))',
  trigger: '#8A93B4',
}
const menuLight = {
  paper: '#FFFFFF',
  border: '#E2E8F0',
  divider: '#EEF2F7',
  text: '#475569',
  textHover: '#0F172A',
  hoverBg: '#F8FAFF',
  selectedBg: '#EEF5FF',
  selectedText: '#1B6FD6',
  trigger: '#94A3B8',
}
const menuSelectedAccent = 'inset 3px 0 0 #2D89F4'

export const useMenuStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      whiteSpace: 'nowrap',
      '&.active': {
        '& .MuiDrawer-paper': {
          width: navbarOpenWidth,
        },
        '& .MuiListItemText-root': {
          padding: 0,
        },
      },
      '&.ltr': {
        '& .MuiListItemButton-root': {
          paddingLeft: 12,
        },
        '& .MuiListItemText-root': {
          paddingLeft: 10,
        },
      },
      '&.rtl': {
        '& .MuiListItemButton-root': {
          paddingRight: 12,
        },
        '& .MuiListItemText-root': {
          paddingRight: 10,
        },
      },
      '& .MuiListItemText-root': {
        transition: 'all 0.3s ease',
      },
      '& .MuiDivider-root': {
        margin: 10,
        background: menuDark.divider,
      },
      '&.light': {
        '& .MuiDrawer-paper': {
          background: menuLight.paper,
          borderRight: `1px solid ${menuLight.border}`,
        },
        '& .MuiDivider-root': {
          background: menuLight.divider,
        },
        '& .MuiIconButton-root': {
          color: menuLight.trigger,
          '&:hover': { color: menuLight.textHover },
        },
        '& .MuiListItemButton-root': {
          '& .MuiTypography-root': { color: menuLight.text },
          '&:hover': {
            background: menuLight.hoverBg,
            '& .MuiTypography-root': { color: menuLight.textHover },
          },
          '&.Mui-selected': {
            background: `${menuLight.selectedBg} !important`,
            '& .MuiTypography-root': { color: `${menuLight.selectedText} !important` },
          },
        },
      },
    },
    paper: {
      background: menuDark.paper,
      width: navbarCloseWidth,
      transition: 'ease 0.3s',
      overflowX: 'hidden',
    },
  })
})

export const useTriggerStyles = makeStyles({
  root: {
    position: 'absolute',
    top: 21,
    color: menuDark.trigger,
    '&:hover': {
      color: '#FFF',
    },
    '&.ltr': {
      right: 13,
    },
    '&.rtl': {
      left: 9,
    },
  },
})

export const useListItemButtonStyles = makeStyles({
  root: {
    margin: '2px 10px',
    width: 'auto',
    borderRadius: '11px',
    transition: 'background .15s ease, color .15s ease',
    '& .MuiListItemIcon-root': {
      minWidth: 35,
    },
    '& .MuiTypography-root': {
      fontSize: 15,
      fontWeight: 500,
      fontFamily: 'Montserrat-SemiBold',
      color: menuDark.text,
      transition: 'color .15s ease',
    },
    '&.rtl': {
      textAlign: 'right',
    },
    '&:hover': {
      background: menuDark.hoverBg,
      '& .MuiTypography-root': {
        color: menuDark.textActive,
      },
    },
  },
  selected: {
    borderRadius: '11px',
    boxShadow: `${menuSelectedAccent} !important`,
    background: `${menuDark.selectedBg} !important`,
    '& .MuiTypography-root': {
      color: `${menuDark.textActive} !important`,
    },
  },
})
